import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Autocomplete, FormControl, Grid, TextField } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DetailedFundingSources from '../../defaults/DetailedFundingSources.json';
import FieldNames from '../../defaults/FieldNames.json';
import BasicTextField from '../details/customPanels/BasicTextField';
import DetailsPopover from '../details/customPanels/DetailsPopover';

const BlueAlien = "#1976d2";

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: '90%'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '90%'
  },
  snackBarStyle: {
    top: '120px !important'
  },
});

const theme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: 'green'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          //initial border
          borderColor: BlueAlien,
        },
        '&:hover $notchedOutline': {
          //border on hover
          borderColor: BlueAlien,
          border: '2px solid'
        },
        '&$focused $notchedOutline': {
          //border on focus
          borderColor: BlueAlien,
          border: '2px solid'
        },
      },
    }
  },
});

export default function RecordFundingSourceAssignment({ 
   userInfo,
   selectedRow,
   setFunctionalAreaSelection,
   setFundingSourceSelection,
   fundingSourceProgramSelection,
   setFundingSourceProgramSelection,
   fundingSourceCategorySelection,
   setFundingSourceCategorySelection,
   fundingSourceSubCategorySelection,
   setFundingSourceSubCategorySelection,
   contributedNotesSelection,
   setContributedNotesSelection,
   rows,
   setIsBil,
   requiredFields,
   isTreatment,
   isActual,
   isPoint,
   setIsBar,
   editableFeature,
   setFundingSourceValid
  }) {
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const [fundingSourceOptions, setFundingSourceOptions] = useState([]);
  const [additionalRequiredFields,setAdditionalRequiredFields] = useState([]);

  const classes = useStyles();

  useEffect(() => {
    const fundingSources = DetailedFundingSources.fundingSources;
    const isUserPostFire = userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes ? true : false;
    const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes ? true : false;
    if(fundingSources && fundingSources.length > 0){
      let featureAttr = editableFeature?.attributes;
      // PostFire
      const postFireFundingSources = fundingSources?.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes && x[FieldNames.detailedFundingSources.isContributed] === FieldNames.yesNo.no);
      let filteredPostFireFundingSources = postFireFundingSources?.filter(x => {
        return (x.FundingSource.includes(featureAttr?.Program));
      })
      // Fuels
      const fuelsFundingSources = fundingSources?.filter(x => x[FieldNames.detailedFundingSources.isFuels] === FieldNames.yesNo.yes && x[FieldNames.detailedFundingSources.isContributed] === FieldNames.yesNo.no);
      let filteredFuelsFundingSources = fuelsFundingSources?.filter(x => {
        const isFeatureInteragency = featureAttr?.[FieldNames.featureFields.isInteragency] === FieldNames.yesNo.yes;
        if(!isFeatureInteragency)
        {
          return x.FundingSource !== "Fuels Interagency Support";
        }
        return true;
      });
      // Contributed
      const contributedFundingSources = fundingSources?.filter(x => x[FieldNames.detailedFundingSources.isContributed] === FieldNames.yesNo.yes);
      
      let finalFundingSources = contributedFundingSources;
      if(isUserPostFire){
        finalFundingSources = finalFundingSources.concat(filteredPostFireFundingSources)
      }

      if(isUserFuels){
        finalFundingSources = finalFundingSources.concat(filteredFuelsFundingSources)
      }

      setFundingSourceOptions(finalFundingSources);
    }
    
    if(rows && rows.length > 0){
      //Both BAR and BIL as true is possible
      const bilRows = rows.filter(x => x[FieldNames.fundingFields.fundingSourceCategory] && x[FieldNames.fundingFields.fundingSourceCategory].includes("BIL"));
      if(bilRows && bilRows.length > 0){
        setIsBil(true);
      } else {
        setIsBil(false);
      }

      const barRows = rows.filter(x => x[FieldNames.fundingFields.fundingSourceCategory] && x[FieldNames.fundingFields.fundingSourceCategory].includes("BAR"));
      if(barRows && barRows.length > 0){
        setIsBar(true);
      } else {
        setIsBar(false);
      }
    } else {
      setIsBil(false);
      setIsBar(false);
    }
  }, [config, rootServiceUrl, rows, setIsBil, setIsBar, userInfo]);

  useEffect(() => {
    setFundingSourceSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingSource]) ? selectedRow[FieldNames.fundingFields.fundingSource] : "");
  }, [selectedRow, setFundingSourceSelection]);

  useEffect(() => {
    setFunctionalAreaSelection((selectedRow && selectedRow[FieldNames.fundingFields.functionalArea]) ? selectedRow[FieldNames.fundingFields.functionalArea] : "");
  }, [selectedRow, setFunctionalAreaSelection])

  useEffect(() => {
    setFundingSourceProgramSelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingSourceProgram]) ? selectedRow[FieldNames.fundingFields.fundingSourceProgram] : "");
}, [selectedRow, setFundingSourceProgramSelection])

  useEffect(() => {
  validateFields();
}, [selectedRow, fundingSourceProgramSelection, fundingSourceCategorySelection, fundingSourceSubCategorySelection, contributedNotesSelection]);
  useEffect(() => {
    const catVal = (selectedRow && selectedRow[FieldNames.fundingFields.fundingSourceCategory]) ? selectedRow[FieldNames.fundingFields.fundingSourceCategory] : "";
    setFundingSourceCategorySelection(catVal);
    if(catVal === FieldNames.budgetBuckets.bil && !additionalRequiredFields.includes(FieldNames.fundingFields.fundingSourceSubCategory))
    {
      setAdditionalRequiredFields([...additionalRequiredFields, FieldNames.fundingFields.fundingSourceSubCategory]);
    }
    else
    {
      setAdditionalRequiredFields(additionalRequiredFields.filter(x => x !== FieldNames.fundingFields.fundingSourceSubCategory));
    }
  }, [selectedRow, setFundingSourceCategorySelection])

  useEffect(() => {
    setFundingSourceSubCategorySelection((selectedRow && selectedRow[FieldNames.fundingFields.fundingSourceSubCategory]) ? selectedRow[FieldNames.fundingFields.fundingSourceSubCategory] : "");
  }, [selectedRow, setFundingSourceSubCategorySelection])

  useEffect(() => {
    setContributedNotesSelection((selectedRow && selectedRow[FieldNames.fundingFields.notes]) ? selectedRow[FieldNames.fundingFields.notes] : "");
  }, [selectedRow, setContributedNotesSelection])

  const getFunctionalArea = (newValue) => {
    const fundingSources = fundingSourceOptions.filter(x => x[FieldNames.fundingSources.fundingSource] === newValue);
    if(fundingSources && fundingSources.length > 0 && fundingSources[0] && fundingSources[0][FieldNames.fundingSources.functionalArea]){
      return fundingSources[0][FieldNames.fundingSources.functionalArea]
    } else {
      return "XXXXXXXXX.XXXXXX";
    }
  }

  const validateFields = () => {
    const addlSubCatRequired = additionalRequiredFields.includes(FieldNames.fundingFields.fundingSourceSubCategory)
    const isValid = (fundingSourceProgramSelection && fundingSourceCategorySelection && (!addlSubCatRequired || (fundingSourceSubCategorySelection && fundingSourceSubCategorySelection !== ""))) === true;
    setFundingSourceValid( isValid);
  };

  const getOptions = (options, isBil, isAll, field, isBar) => {
    let finalOptions = [];
    options.forEach(option => {
      let isParentMatch = false;
      if(field === FieldNames.detailedFundingSources.fundingSourceProgram){
        isParentMatch = true;
      } else if(field === FieldNames.detailedFundingSources.fundingSourceCategory){
        if(option[FieldNames.detailedFundingSources.fundingSourceProgram] === fundingSourceProgramSelection){
          isParentMatch = true;
        }
      } else if(field === FieldNames.detailedFundingSources.fundingSourceSubCategory){
        if(option[FieldNames.detailedFundingSources.fundingSourceCategory] === fundingSourceCategorySelection && option[FieldNames.detailedFundingSources.fundingSourceProgram] === fundingSourceProgramSelection){
          isParentMatch = true;
        }
      }

      if(isAll){
        //Return everything (Typically when they only have contributed or nothing yet)
        if(!finalOptions.includes(option[field]) && isParentMatch){
          finalOptions.push(option[field])
        }
      } else {
        if(isBil){
          //If it's bil we only want other bil options (Ignore BAR without BIL too)
          if(option && ((option[FieldNames.detailedFundingSources.fundingSourceCategory] && option[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL")) || option[FieldNames.fundingSources.isContributed] === `${FieldNames.yesNo.yes}`)){
              if(!finalOptions.includes(option[field]) && isParentMatch){
                finalOptions.push(option[field]);
              }
          }
        } else {
          if(isBar) {
            //If it's BAR, but without BIL we only want other BAR or Contributed
            if(option && ((option[FieldNames.detailedFundingSources.fundingSourceCategory] && option[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR") && 
              !option[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL")) || option[FieldNames.fundingSources.isContributed] === `${FieldNames.yesNo.yes}`)){
              if(!finalOptions.includes(option[field]) && isParentMatch){
                finalOptions.push(option[field]);
              }
            }
          } else {
            //Not BIL, Not BAR, and not just contributed
            if(option && ((option[FieldNames.detailedFundingSources.fundingSourceCategory] && !option[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL") && 
              !option[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR")) || option[FieldNames.fundingSources.isContributed] === `${FieldNames.yesNo.yes}`)){
              if(!finalOptions.includes(option[field]) && isParentMatch){
                finalOptions.push(option[field]);
              }
            }
          }
        }
      }
    });
    return finalOptions;
  }


  return (
    <Fragment>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Source Program</label>
                {requiredFields.includes(FieldNames.featureFields.fundingSourceProgram) &&
                  <span className="required">*</span>
                }
                <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingSourceProgram} isTreatment={isTreatment} categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual} agencyValue={null} isBulkApproval={false} />
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                id="programSelection"
                value={fundingSourceProgramSelection}
                onChange={(event, newValue) => {
                  //Only options are Fuels, or Contributed
                  if(!newValue){
                    setFundingSourceCategorySelection("");
                    setFundingSourceSubCategorySelection("");
                    setContributedNotesSelection("");
                    if(!rows || (rows && rows.length === 0) || (rows && rows.length === 1 && selectedRow)){
                      //Setting isbil and isbar back to defaults when the Program is cleared
                      setIsBil(false);
                      setIsBar(false);
                    }
                  }
                  if(newValue && newValue !== fundingSourceProgramSelection){
                    setFundingSourceCategorySelection("");
                    setFundingSourceSubCategorySelection("");
                    setContributedNotesSelection("");
                  }
                  setFundingSourceProgramSelection(newValue);
                }}
                isOptionEqualToValue={(option, value) => option[FieldNames.detailedFundingSources.fundingSourceProgram] === value}
                filterOptions={(options, state) => {
                  //For Program we always return all -> Only options are Fuels, Post-Fire, and Contributed
                  let tempIsBil = false;
                  let tempIsBar = false;
                  let tempIsAll = false;
                  let finalOptions = options;
                  if(rows?.length > 0){
                    let remainingRows = rows.filter(x => x[FieldNames.fundingFields.id] !== selectedRow?.[FieldNames.fundingFields.id]);
                    if(remainingRows?.length){
                      let nonBilRemainingRows = remainingRows?.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && !x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL") && 
                        !x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));
                      if(nonBilRemainingRows?.length > 0){
                        //Fuels or Post Fire Non-BIL
                        //Non-BIL rows Remain - We don't allow non-bil funding and non-bil post fire to coexist
                        const nonBilRemainingRowsPostFire = nonBilRemainingRows?.filter(x => x[FieldNames.detailedFundingSources.fundingSourceProgram] === FieldNames.fundingSourcePrograms.postFire)
                        let tempOptions = options;
                        if(nonBilRemainingRowsPostFire?.length){
                          //Post Fire non-BIL
                          tempOptions = options.filter(x => (x[FieldNames.detailedFundingSources.isBil] !== FieldNames.yesNo.yes && x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes) || x[FieldNames.detailedFundingSources.isContributed] === FieldNames.yesNo.yes)
                        } else {
                          //Fuels non-BIL
                          tempOptions = options.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.no || x[FieldNames.detailedFundingSources.isContributed] === FieldNames.yesNo.yes)
                        }
                        finalOptions = tempOptions;
                      } else {
                        tempIsAll = true;
                      }
                    } else {
                      tempIsAll = true;
                    }
                  } else {
                    tempIsAll = true;
                  }
                  return getOptions(finalOptions, tempIsBil, tempIsAll, FieldNames.detailedFundingSources.fundingSourceProgram, tempIsBar);
                }}
                getOptionLabel={(option) => { 
                  return option;
                }}
                options={fundingSourceOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Source Category</label>
                {requiredFields.includes(FieldNames.featureFields.fundingSourceCategory) &&
                  <span className="required">*</span>
                }
                <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingSourceCategory} isTreatment={isTreatment} categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual} agencyValue={null} isBulkApproval={false} />
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                id="categorySelection"
                value={fundingSourceCategorySelection}
                onChange={(event, newValue) => {
                  if(!newValue){
                    setFundingSourceSelection("");
                    setFunctionalAreaSelection("");
                    setFundingSourceSubCategorySelection("");
                    setFundingSourceCategorySelection(newValue);

                    if(!rows || (rows && rows.length === 0) || (rows && rows.length === 1 && selectedRow)){
                      //Setting isbil and isbar back to defaults when the value is cleared
                      setIsBil(false);
                      setIsBar(false);
                      setAdditionalRequiredFields(additionalRequiredFields.filter(x => x !== FieldNames.fundingFields.fundingSourceSubCategory));
                    }

                  } else {
                    //Value is selected, not cleared.  The value isn't a BIL or a BAR (BAR is only possible on post fire)
                    setFundingSourceCategorySelection(newValue);

                    if(newValue && (!newValue.includes("BIL") || fundingSourceProgramSelection === FieldNames.fundingSourcePrograms.postFire)){
                      setFundingSourceSubCategorySelection("");
                      const currentRecordSelected = fundingSourceOptions.filter(x => x[FieldNames.detailedFundingSources.fundingSourceProgram] === fundingSourceProgramSelection && x[FieldNames.detailedFundingSources.fundingSourceCategory] === newValue);
                      if(currentRecordSelected && currentRecordSelected.length > 0){
                        //Get the row selected and set the FundingSourceSelection and the FunctionalAreaSelection
                        setFundingSourceSelection(currentRecordSelected[0][FieldNames.detailedFundingSources.fundingSource]);
                        setFunctionalAreaSelection(currentRecordSelected[0][FieldNames.detailedFundingSources.functionalArea])
                      }
                      else
                      {
                        setFundingSourceSelection("");
                        setFunctionalAreaSelection("");
                      }
                      setIsBil(false);
                      setIsBar(false);
                      setAdditionalRequiredFields(additionalRequiredFields.filter(x => x !== FieldNames.fundingFields.fundingSourceSubCategory));
                    } else {
                      //Both IsBil and IsBar can be true
                      if(newValue.includes("BIL")){
                        setIsBil(true);
                      } else {
                        setIsBil(false)
                      }
                      if(newValue.includes("BAR")){
                        setIsBar(true);
                      } else {
                        setIsBar(false)
                      }
                      setFundingSourceSelection("");
                      setFunctionalAreaSelection("");
                      setAdditionalRequiredFields([...additionalRequiredFields, FieldNames.fundingFields.fundingSourceSubCategory]);
                    }

                  }
                }}
                isOptionEqualToValue={(option, value) => option[FieldNames.detailedFundingSources.fundingSourceCategory] === value}
                filterOptions={(options, state) => {
                  let tempIsBil = false;
                  let tempIsBar = false;
                  let tempIsAll = false;
                  let finalOptions = options;
                  if(!rows || (rows && rows.length === 0)){
                    //We have no rows so it can't be BIL or Non-BIL yet - return all
                    tempIsAll = true;
                  } else if(rows && rows.length === 1 && selectedRow){
                    //We have only 1 row and we are editing it - return all - this is the only row that will determine if it's bil or non-bil
                    tempIsAll = true;
                  } else {
                    //All other cases, could be we have 1 row and we are adding a new one, Could be we have 5 rows
                    //Need to account for 2 rows -> 1 could be contributed and the one we are editing could have been the bil
                    if(rows && rows.length > 0 && selectedRow){
                      //We are editing a row - filter out the one we are editing - there will be at least 1 left
                      let remainingRows = rows.filter(x => x[FieldNames.fundingFields.id] !== selectedRow[FieldNames.fundingFields.id]);
                      if(remainingRows?.length > 0){
                        //5 types of rows
                        let contributedRows = remainingRows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceProgram] && x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));
                        let bilRemainingRows = remainingRows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL")));
                        let nonBilRemainingRows = remainingRows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && !x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL") && 
                          !x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));
                        let barRemainingRows = remainingRows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR")))
                        let nonBarRemainingRows = remainingRows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && !x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR") && 
                          !x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));

                        if(remainingRows.length === contributedRows?.length){
                          //Only contributed rows remaining so only look at current selection
                          //Fuels or PostFire has been selected - they must choose between BIL and non-BIL options Show all or They want another contributed line
                          tempIsBil = false;
                          tempIsBar = false;
                          tempIsAll = true;
                        } else if(bilRemainingRows?.length > 0) {
                          //BIL rows Remain
                          tempIsBil = true;
                        } else if(barRemainingRows?.length > 0) {
                          tempIsBar = true;
                        } else if(nonBilRemainingRows?.length > 0){
                          //Non-BIL rows Remain - We don't allow non-bil funding and non-bil post fire to coexist
                          const nonBilRemainingRowsPostFire = nonBilRemainingRows?.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes)
                          let tempOptions = options;
                          if(nonBilRemainingRowsPostFire){
                            tempOptions = options.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes)
                          } else {
                            tempOptions = options.filter(x => x[FieldNames.detailedFundingSources.isFuels] === FieldNames.yesNo.yes)
                          }
                          finalOptions = tempOptions;
                          tempIsBil = false;
                        } else if(nonBarRemainingRows?.length > 0){
                          //Non-BIL rows Remain - We don't allow non-bil funding and non-bil post fire to coexist
                          const nonBarRemainingRowsPostFire = nonBarRemainingRows?.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes)
                          let tempOptions = options;
                          if(nonBarRemainingRowsPostFire){
                            tempOptions = options.filter(x => x[FieldNames.detailedFundingSources.isPostFire] === FieldNames.yesNo.yes)
                          } else {
                            tempOptions = options.filter(x => x[FieldNames.detailedFundingSources.isFuels] === FieldNames.yesNo.yes)
                          }
                          finalOptions = tempOptions;
                          tempIsBar = false;
                        }
                        //We should never have both bil and non-bil

                        if(bilRemainingRows?.length > 0){
                          setIsBil(true);
                        } else {
                          setIsBil(false);
                        }

                        if(barRemainingRows?.length > 0){
                          setIsBar(true)
                        } else {
                          setIsBar(false)
                        }
                      } else {
                        //No remaining rows
                        if(fundingSourceProgramSelection && !fundingSourceProgramSelection.includes("Fuels") && fundingSourceCategorySelection && fundingSourceCategorySelection.includes("BIL")){
                          tempIsBil = true;
                        } else if(fundingSourceProgramSelection && !fundingSourceProgramSelection.includes("Fuels") && fundingSourceCategorySelection && fundingSourceCategorySelection.includes("BAR")){
                          tempIsBar = false;
                        } else {
                          tempIsBil = false;
                          tempIsBar = false;
                        }
                      }
                    } else {
                      //There are rows and we aren't editing
                      //3 types of rows
                      let contributedRows = rows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceProgram] && x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));
                      let bilRemainingRows = rows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL")));
                      let nonBilRemainingRows = rows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && !x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BIL") && 
                          !x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));
                      let barRemainingRows = rows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR")))
                      let nonBarRemainingRows = rows.filter(x => (x[FieldNames.detailedFundingSources.fundingSourceCategory] && !x[FieldNames.detailedFundingSources.fundingSourceCategory].includes("BAR") && 
                          !x[FieldNames.detailedFundingSources.fundingSourceProgram].includes("Contributed")));

                      if((!bilRemainingRows || (bilRemainingRows?.length === 0)) && (!nonBilRemainingRows || (nonBilRemainingRows?.length === 0))){
                        //Only contributed
                        tempIsBil = false;
                        tempIsBar = false;
                        tempIsAll = true;
                      } else if(bilRemainingRows?.length > 0){
                        tempIsBil = true;
                      } else if(barRemainingRows?.length > 0){
                        tempIsBar = true;
                      } else if(nonBilRemainingRows?.length > 0){
                        tempIsBil = false;
                      } else if(nonBarRemainingRows?.length > 0){
                        tempIsBar = false;
                      } else {
                        tempIsAll = true;
                      }
                    }
                  }
                  return getOptions(finalOptions, tempIsBil, tempIsAll, FieldNames.detailedFundingSources.fundingSourceCategory, tempIsBar);
                }}
                getOptionLabel={(option) => { 
                  return option;
                }}
                options={fundingSourceOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      {(fundingSourceProgramSelection && fundingSourceProgramSelection.includes("Contributed")) &&
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Notes</label>
                {requiredFields.includes(FieldNames.featureFields.contributedNotes) &&
                  <span className="required">*</span>
                }
                <DetailsPopover mode={"edit"} field={FieldNames.featureFields.contributedNotes} isTreatment={isTreatment} categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual} agencyValue={null} isBulkApproval={false} />
              </span>
            </Grid>
            <Grid item size={6}>
              <BasicTextField
                field="contributedNotes"
                label="Contributed Notes"
                value={contributedNotesSelection}
                setValue={setContributedNotesSelection}
                variant={"outlined"}
                placeholder={"Enter notes"}
                useFullWidth={true}
                useMultiline={true}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      }
      {(fundingSourceCategorySelection && fundingSourceCategorySelection.includes("BIL") && !fundingSourceProgramSelection.includes("Post-Fire")) &&
        <FormControl variant="standard" className={classes.formControl}>
          <ThemeProvider theme={theme}>
            <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
              <Grid item size={6}>
                <span>
                  <label>Funding Source SubCategory</label>
                  {(requiredFields.includes(FieldNames.featureFields.fundingSourceSubCategory) ||
                    additionalRequiredFields.includes(FieldNames.featureFields.fundingSourceSubCategory)) &&
                    <span className="required">*</span>
                  }
                  <DetailsPopover mode={"edit"} field={FieldNames.featureFields.fundingSourceSubCategory} isTreatment={isTreatment} categoryValue={null} isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no} isActuals={isActual} agencyValue={null} isBulkApproval={false} />
                </span>
              </Grid>
              <Grid item size={6}>
                <Autocomplete
                  id="subCategorySelection"
                  value={fundingSourceSubCategorySelection}
                  onChange={(event, newValue) => {
                    //Only options the BIL SubCategories
                    setFundingSourceSubCategorySelection(newValue);

                  
                    const currentRecordSelected = fundingSourceOptions.filter(x => x[FieldNames.detailedFundingSources.fundingSourceProgram] === fundingSourceProgramSelection && 
                      x[FieldNames.detailedFundingSources.fundingSourceCategory] === fundingSourceCategorySelection && 
                      x[FieldNames.detailedFundingSources.fundingSourceSubCategory] === newValue);
                    if(currentRecordSelected && currentRecordSelected.length > 0){
                      //Get the row selected and set the FundingSourceSelection and the FunctionalAreaSelection
                      setFundingSourceSelection(currentRecordSelected[0][FieldNames.detailedFundingSources.fundingSource]);
                      setFunctionalAreaSelection(currentRecordSelected[0][FieldNames.detailedFundingSources.functionalArea])
                    }
                    else
                    {
                      setFundingSourceSelection("");
                      setFunctionalAreaSelection("")
                    }
                    //Get the row selected and set the FundingSourceSelection and the FunctionalAreaSelection
                  }}
                  isOptionEqualToValue={(option, value) => option[FieldNames.detailedFundingSources.fundingSourceSubCategorySelection] === value}
                  filterOptions={(options, state) => {
                    //Return all.  This already won't show unless Category is bil and it's not post-fire
                    return getOptions(options, false, true, FieldNames.detailedFundingSources.fundingSourceSubCategory, false);
                  }}
                  getOptionLabel={(option) => { 
                    return option;
                  }}
                  options={fundingSourceOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params?.InputProps?.endAdornment}
                            </React.Fragment>
                          )
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </ThemeProvider>
        </FormControl>
      }
    </Fragment>
  );
}